/**
 * @CODE:SPEC-ACC-001-HOOKS-001
 * useAccommodationSearch Hook
 *
 * Custom hook for accommodation search with React Query integration
 *
 * Features:
 * - Manages search state with filters
 * - React Query integration for caching and background updates
 * - URL params synchronization
 * - Pagination management
 */

import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { searchAccommodations } from "@/features/accommodation/api";
import {
  searchAccommodationsByCourse,
  searchAccommodationsByTrail,
} from "@/features/accommodation/api/accommodation-trail.api";
import type {
  AccommodationFilters,
  AccommodationSearchResponse,
} from "@/features/accommodation/types";

interface UseAccommodationSearchOptions {
  enabled?: boolean;
  initialFilters?: AccommodationFilters;
}

export function useAccommodationSearch(
  options: UseAccommodationSearchOptions = {},
) {
  const { enabled = true, initialFilters } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Build filters from URL params or initial values
  const filters = useMemo<AccommodationFilters>(() => {
    return {
      province: searchParams.get("province") || initialFilters?.province,
      district: searchParams.get("district") || initialFilters?.district,
      accommodation_type:
        searchParams.get("type") || initialFilters?.accommodation_type,
      accommodation_type_id: searchParams.get("typeId")
        ? Number(searchParams.get("typeId"))
        : initialFilters?.accommodation_type_id,
      trail_id: searchParams.get("trailId") || initialFilters?.trail_id,
      course_id: searchParams.get("courseId") || initialFilters?.course_id,
      distance_from_course_km: searchParams.get("distance")
        ? Number(searchParams.get("distance"))
        : initialFilters?.distance_from_course_km,
      priceFrom: searchParams.get("priceFrom")
        ? Number(searchParams.get("priceFrom"))
        : initialFilters?.priceFrom,
      priceTo: searchParams.get("priceTo")
        ? Number(searchParams.get("priceTo"))
        : initialFilters?.priceTo,
      has_pickup: searchParams.get("hasPickup")
        ? searchParams.get("hasPickup") === "true"
        : initialFilters?.has_pickup,
      search: searchParams.get("search") || initialFilters?.search,
      sortBy: (searchParams.get("sortBy") as any) || initialFilters?.sortBy,
      sortOrder:
        (searchParams.get("sortOrder") as any) || initialFilters?.sortOrder,
      page: searchParams.get("page")
        ? Number(searchParams.get("page"))
        : (initialFilters?.page ?? 1),
      limit: initialFilters?.limit ?? 12,
    };
  }, [searchParams, initialFilters]);

  // React Query hook - uses different API based on filter type
  const query = useQuery<AccommodationSearchResponse>({
    queryKey: ["accommodations", filters],
    queryFn: async () => {
      // Trail/Course-based search
      if (filters.trail_id || filters.course_id) {
        const page = filters.page ?? 1;
        const limit = filters.limit ?? 12;

        let result;

        if (filters.course_id) {
          // Search by specific course
          result = await searchAccommodationsByCourse({
            courseId: filters.course_id,
            maxDistanceKm: filters.distance_from_course_km ?? 10,
            accommodationTypeId: filters.accommodation_type_id,
            minPrice: filters.priceFrom,
            maxPrice: filters.priceTo,
            hasPickup: filters.has_pickup,
          });
        } else if (filters.trail_id) {
          // Search by trail (all courses)
          result = await searchAccommodationsByTrail({
            trailId: filters.trail_id,
            maxDistanceKm: filters.distance_from_course_km ?? 10,
            accommodationTypeId: filters.accommodation_type_id,
            minPrice: filters.priceFrom,
            maxPrice: filters.priceTo,
            hasPickup: filters.has_pickup,
          });
        }

        if (!result) {
          return { data: [], total: 0, page, limit, totalPages: 0 };
        }

        // Manual pagination (API returns all results)
        const offset = (page - 1) * limit;
        const paginatedData = result.data.slice(offset, offset + limit);
        const totalPages = Math.ceil(result.count / limit);

        return {
          data: paginatedData,
          total: result.count,
          page,
          limit,
          totalPages,
        };
      }

      // Traditional province/district-based search
      return searchAccommodations(filters);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  // Helper to update filters and URL
  const updateFilters = useCallback(
    (newFilters: Partial<AccommodationFilters>) => {
      const params = new URLSearchParams();

      const updatedFilters = { ...filters, ...newFilters, page: 1 };

      if (updatedFilters.province)
        params.set("province", updatedFilters.province);
      if (updatedFilters.district)
        params.set("district", updatedFilters.district);
      if (updatedFilters.accommodation_type)
        params.set("type", updatedFilters.accommodation_type);
      if (updatedFilters.accommodation_type_id)
        params.set("typeId", String(updatedFilters.accommodation_type_id));
      if (updatedFilters.trail_id)
        params.set("trailId", updatedFilters.trail_id);
      if (updatedFilters.course_id)
        params.set("courseId", updatedFilters.course_id);
      if (updatedFilters.distance_from_course_km)
        params.set("distance", String(updatedFilters.distance_from_course_km));
      if (updatedFilters.priceFrom)
        params.set("priceFrom", String(updatedFilters.priceFrom));
      if (updatedFilters.priceTo)
        params.set("priceTo", String(updatedFilters.priceTo));
      if (updatedFilters.has_pickup)
        params.set("hasPickup", "true");
      if (updatedFilters.search) params.set("search", updatedFilters.search);
      if (updatedFilters.sortBy) params.set("sortBy", updatedFilters.sortBy);
      if (updatedFilters.sortOrder)
        params.set("sortOrder", updatedFilters.sortOrder);
      params.set("page", "1");

      setSearchParams(params);
    },
    [filters, setSearchParams],
  );

  // Helper to navigate to page
  const goToPage = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", String(page));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  return {
    accommodations: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page: filters.page ?? 1,
    limit: filters.limit ?? 12,
    totalPages: query.data?.totalPages ?? 0,
    filters,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    updateFilters,
    goToPage,
  };
}
